/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import { BlockButton, Box, Flex, IconLoading } from "@powerledger/ui-component-lib";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import { Form } from "@/app/components/form";
import { LegalCheckBoxes } from "@/app/components/legal-check-boxes";
import { TRACEX_COUNTRY_FLAGS } from "@/app/lib/country-flag-helpers";

import { StepButton } from "../step-button";
import { StepLayout } from "../step-layout";
import { RegionSelectStepProps } from "./region-selection-step.types";

export const RegionSelectionStepView: FC<RegionSelectStepProps> = ({
  handleSubmit,
  saveMarket,
  goNext,
  isValid,
  isLoading,
  tenants,
  setFieldValue,
  regionSelectionValues,
  handleAcknowledgementChange,
  isLegalCheckBoxDisabled,
}) => {
  const { t } = useTranslation();

  return (
    <Form onSubmit={handleSubmit} sx={{ height: "100%" }}>
      <StepLayout
        heading={t("Select the market")}
        id="RegionSelects"
        actions={
          <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
            <StepButton
              onClick={async () => {
                await saveMarket(goNext);
              }}
              disabled={!isValid}
            />
          </Flex>
        }
      >
        {isLoading ? (
          <IconLoading sx={{ ml: 2 }} />
        ) : (
          <>
            <Flex sx={{ flexWrap: "wrap" }}>
              {tenants.map((tenant) => (
                <BlockButton
                  key={tenant.id}
                  active={tenant.id === regionSelectionValues.tenantUid}
                  displayName={tenant.displayName ? t(tenant.displayName) : ""}
                  onClick={() => {
                    if (tenant.id !== regionSelectionValues.tenantUid) {
                      setFieldValue("termsAccepted", false);
                      setFieldValue("privacyPolicyAccepted", false);
                      setFieldValue("rulesAccepted", false);
                    }
                    setFieldValue("tenantUid", tenant.id);
                  }}
                  sx={{
                    borderColor: tenant.id === regionSelectionValues.tenantUid ? "primary.500" : "",
                  }}
                >
                  <Box
                    sx={{ width: "50%" }}
                    aria-label={`${tenant.localisation?.countryCode.toLowerCase() || ""} flag`}
                  >
                    {
                      TRACEX_COUNTRY_FLAGS[
                        tenant.localisation?.countryCode ? tenant.localisation?.countryCode.toLowerCase() : ""
                      ]
                    }
                  </Box>
                </BlockButton>
              ))}
            </Flex>

            {regionSelectionValues.tenantUid && (
              <LegalCheckBoxes
                handleAcknowledgementChange={handleAcknowledgementChange}
                isCheckBoxDisabled={isLegalCheckBoxDisabled}
                showLegalMessage
              />
            )}
          </>
        )}
      </StepLayout>
    </Form>
  );
};
